import { put, takeEvery } from 'redux-saga/effects';
import {
    fetchOrderBookingActivitiesSuccess,
    fetchOrderBookingActivitiesFailure,
} from 'store/actions/orderActivitiesActions';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import * as types from 'store/actions/types';
import { has, uniq } from 'lodash';
import utils from 'helpers/utils';

function* fetchOrderBookingActivitiesAsync({ payload }) {
    const {
        id,
        onError = null,
        onSuccess = null,
    } = payload;

    const url = `${ config.getApiBaseUrl() }/bookings/${ id }/activities`;

    try {
        const activities = yield apiClient.read(url);

        const reservationIds = uniq(activities.filter(({ target_type }) => {
            return target_type === 'reservation';
        }).map(({ target_id }) => {
            return +target_id;
        }));

        const sessionsIds = yield Promise.all(reservationIds.map(async (reservationId) => {
            const sessions = await apiClient.read(`${ config.getApiBaseUrl() }/reservations/${ reservationId }/sessions`, {
                size: 96,
                offset: 0,
                sort_by: 'created_at',
                sort_direction: types.SORT_DESC,
            });

            return sessions.map(({ id }) => {
                return id;
            });
        })).catch(() => {
            return [];
        }).finally((sessions = []) => {
            return sessions;
        });

        const hardwareLogsActivities = yield Promise.all(sessionsIds.flat().map(async (sessionId) => {
            const response = await apiClient.read(`${ config.getApiBaseUrl() }/hardware-logs`, {
                session_id: sessionId,
                size: 96,
                offset: 0,
                sort_by: 'created_at',
                sort_direction: 'desc',
            });

            return response.results || [];
        })).then((hardwareLogsCollection) => {
            const hardwareLogs = hardwareLogsCollection.flat();

            const getHardwareActivityIdentifier = (hardwareLog) => {
                if (has(hardwareLog, 'hardware_state.locked')) {
                    return hardwareLog.hardware_state.locked ? 'session-lock-closed' : 'session-lock-opened';
                }

                return has(hardwareLog, 'hardware_state.gps') ? 'gps' : 'hardware-activity';
            };

            return hardwareLogs.map((hardwareLog) => {
                return {
                    activity: getHardwareActivityIdentifier(hardwareLog),
                    actor: null,
                    created_at: hardwareLog.created_at,
                    id: utils.uuid(),
                    properties: {
                        'hardware state': Object.fromEntries(
                            Object.entries(hardwareLog.hardware_state).map(([
                                key,
                                value,
                            ]) => {
                                return [
                                    key.replace(/_/g, ' '),
                                    value,
                                ];
                            })
                        ),
                    },
                    status: 'success',
                    target_id: hardwareLog.session_id,
                    target_type: 'session',
                };
            });
        }).catch(() => {
            return [];
        }).finally((hardwareLogsActivities = []) => {
            return hardwareLogsActivities;
        });

        const result = {
            id: id,
            response: [
                ...activities,
                ...hardwareLogsActivities,
            ].sort((activityA, activityB) => {
                return new Date(activityB.created_at) - new Date(activityA.created_at);
            }),
        };

        yield put(fetchOrderBookingActivitiesSuccess({
            id: id,
            response: [
                ...activities,
                ...hardwareLogsActivities,
            ].sort((activityA, activityB) => {
                return new Date(activityB.created_at) - new Date(activityA.created_at);
            }),
        }));

        if (utils.isFunction(onSuccess)) {
            yield onSuccess(result);
        }
    } catch (error) {
        yield put(fetchOrderBookingActivitiesFailure(error));

        if (utils.isFunction(onError)) {
            yield onError(error);
        }
    }
}

export default function* orderActivitiesSagas() {
    yield [
        takeEvery(types.FETCH_ORDER_BOOKING_ACTIVITIES, fetchOrderBookingActivitiesAsync),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/orderActivitiesSagas.js